<template>
  <v-dialog v-model="dialog" max-width="1200px" persistent class="modern-dialog">
    <v-card class="modern-dialog-card">
      <v-card-title class="d-flex align-center modern-dialog-title">
        <v-icon left large color="primary">mdi-chart-line</v-icon>
        <span class="text-h5">Análise de Congestionamento por Horário</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-container fluid>
          <!-- Gráfico Geral - Total de Atualizações por Horário -->
          <v-row>
            <v-col cols="12">
              <v-card outlined class="modern-chart-card">
                <v-card-title class="text-h6">
                  <v-icon left color="primary">mdi-chart-line</v-icon>
                  Total de Atualizações por Horário
                </v-card-title>
                <v-card-text>
                  <div class="chart-container" style="height: 300px;">
                    <!-- Gráfico manual principal -->
                    <canvas 
                      v-if="!loading && dadosCongestionamento.length > 0"
                      :id="'main-chart-' + chartKey"
                      width="800" 
                      height="300"
                      :style="getCanvasStyle()"
                    ></canvas>
                    <v-skeleton-loader v-else-if="loading" type="chart"></v-skeleton-loader>
                    <div v-else class="d-flex align-center justify-center" style="height: 100%;">
                      <v-alert type="info" outlined>
                        <div class="text-center">
                          <v-icon size="48" color="info">mdi-chart-line</v-icon>
                          <div class="text-h6 mt-2">
                            {{ !chartJsInitialized ? 'Inicializando gráficos...' : (dadosCongestionamento.length === 0 ? 'Nenhum dado de congestionamento disponível' : 'Carregando gráfico...') }}
                          </div>
                          <div class="text-body-2 mt-1">
                            Dados carregados: {{ dadosCongestionamento.length }} registros
                          </div>
                        </div>
                      </v-alert>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Mensagem quando não há dados -->
          <v-row v-if="!loading && dadosCongestionamento.length === 0">
            <v-col cols="12">
              <v-card outlined>
                <v-card-text class="text-center pa-8">
                  <v-icon size="64" color="grey lighten-1">mdi-chart-line</v-icon>
                  <div class="text-h6 mt-4 grey--text">
                    Nenhum dado de congestionamento encontrado
                  </div>
                  <div class="text-body-2 grey--text mt-2">
                    Configure cronogramas com horários para visualizar a análise de congestionamento
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Tabela de Resumo -->
          <v-row>
            <v-col cols="12">
              <v-card outlined class="modern-chart-card">
                <v-card-title class="text-h6">
                  <v-icon left color="success">mdi-table</v-icon>
                  Resumo Detalhado
                </v-card-title>
                <v-card-text>
                  <v-data-table
                    v-if="dadosCongestionamento.length > 0"
                    :headers="tableHeaders"
                    :items="dadosCongestionamento"
                    :items-per-page="10"
                    class="modern-table"
                    :class="{ 'table-dark': $vuetify.theme.dark }"
                    dense
                  >
                    <!-- Cabeçalhos personalizados com apenas ícones e tooltips -->
                    <template v-slot:header.vcon="{ header }">
                      <div class="d-flex justify-center">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <div class="system-icon-container-header" v-bind="attrs" v-on="on">
                              <img src="/ico_vcon.png" alt="VCON" class="system-icon-header" />
                            </div>
                          </template>
                          <span>VCON</span>
                        </v-tooltip>
                      </div>
                    </template>

                    <template v-slot:header.vpra="{ header }">
                      <div class="d-flex justify-center">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <div class="system-icon-container-header" v-bind="attrs" v-on="on">
                              <img src="/ico_vpra.png" alt="VPRA" class="system-icon-header" />
                            </div>
                          </template>
                          <span>VPRA</span>
                        </v-tooltip>
                      </div>
                    </template>

                    <template v-slot:header.vsuc="{ header }">
                      <div class="d-flex justify-center">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <div class="system-icon-container-header" v-bind="attrs" v-on="on">
                              <img src="/ico_vsuc.png" alt="VSUC" class="system-icon-header" />
                            </div>
                          </template>
                          <span>VSUC</span>
                        </v-tooltip>
                      </div>
                    </template>

                    <template v-slot:header.vsup="{ header }">
                      <div class="d-flex justify-center">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <div class="system-icon-container-header" v-bind="attrs" v-on="on">
                              <img src="/ico_vsup.png" alt="VSUP" class="system-icon-header" />
                            </div>
                          </template>
                          <span>VSUP</span>
                        </v-tooltip>
                      </div>
                    </template>

                    <template v-slot:header.vsyn="{ header }">
                      <div class="d-flex justify-center">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <div class="system-icon-container-header" v-bind="attrs" v-on="on">
                              <img src="/ico_vsyn.png" alt="VSYN" class="system-icon-header" />
                            </div>
                          </template>
                          <span>VSYN</span>
                        </v-tooltip>
                      </div>
                    </template>
                    <template v-slot:item.hora_minuto="{ item }">
                      <v-chip color="primary" outlined small>
                        {{ item.hora_minuto }}
                      </v-chip>
                    </template>

                    <template v-slot:item.total="{ item }">
                      <v-chip color="primary" small>
                        {{ item.total }}
                      </v-chip>
                    </template>

                    <template v-slot:item.vcon="{ item }">
                      <v-chip 
                        :style="{ borderColor: getSystemMainColor('VCON'), color: getSystemMainColor('VCON') }" 
                        outlined
                        small
                      >
                        {{ item.vcon }}
                      </v-chip>
                    </template>

                    <template v-slot:item.vpra="{ item }">
                      <v-chip 
                        :style="{ borderColor: getSystemMainColor('VPRA'), color: getSystemMainColor('VPRA') }" 
                        outlined
                        small
                      >
                        {{ item.vpra }}
                      </v-chip>
                    </template>

                    <template v-slot:item.vsuc="{ item }">
                      <v-chip 
                        :style="{ borderColor: getSystemMainColor('VSUC'), color: getSystemMainColor('VSUC') }" 
                        outlined
                        small
                      >
                        {{ item.vsuc }}
                      </v-chip>
                    </template>

                    <template v-slot:item.vsup="{ item }">
                      <v-chip 
                        :style="{ borderColor: getSystemMainColor('VSUP'), color: getSystemMainColor('VSUP') }" 
                        outlined
                        small
                      >
                        {{ item.vsup }}
                      </v-chip>
                    </template>

                    <template v-slot:item.vsyn="{ item }">
                      <v-chip 
                        :style="{ borderColor: getSystemMainColor('VSYN'), color: getSystemMainColor('VSYN') }" 
                        outlined
                        small
                      >
                        {{ item.vsyn }}
                      </v-chip>
                    </template>

                    <template v-slot:item.percentual="{ item }">
                      <v-progress-linear
                        :value="getPercentual(item)"
                        :color="getPercentualColor(item)"
                        height="20"
                        rounded
                      >
                        <template v-slot:default="{ value }">
                          <strong>{{ Math.round(value) }}%</strong>
                        </template>
                      </v-progress-linear>
                    </template>
                  </v-data-table>
                  
                  <!-- Loading ou mensagem quando não há dados -->
                  <div v-else class="text-center pa-8">
                    <v-skeleton-loader v-if="loading" type="table"></v-skeleton-loader>
                    <div v-else>
                      <v-icon size="48" color="grey lighten-1">mdi-table</v-icon>
                      <div class="text-h6 mt-4 grey--text">
                        Nenhum dado para exibir
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="dialog = false">
          Fechar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { getSystemMainColor } from '@/utils/systemColors'

export default {
  name: 'CongestionamentoViewer',
  components: {
  },
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dadosCongestionamento: [],
      loading: false,
      maxTotal: 0,
      tableHeaders: [
        { text: 'Horário', value: 'hora_minuto', sortable: true },
        { text: 'Total', value: 'total', sortable: true, align: 'center' },
        { text: 'VCON', value: 'vcon', sortable: true, align: 'center', icon: '/ico_vcon.png' },
        { text: 'VPRA', value: 'vpra', sortable: true, align: 'center', icon: '/ico_vpra.png' },
        { text: 'VSUC', value: 'vsuc', sortable: true, align: 'center', icon: '/ico_vsuc.png' },
        { text: 'VSUP', value: 'vsup', sortable: true, align: 'center', icon: '/ico_vsup.png' },
        { text: 'VSYN', value: 'vsyn', sortable: true, align: 'center', icon: '/ico_vsyn.png' },
        { text: 'Congestionamento', value: 'percentual', sortable: true, align: 'center' }
      ],
      chartKey: 0,
      charts: [], // Array para armazenar referências dos gráficos
      chartJsInitialized: false
    }
  },
  computed: {
    dialog: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    }
  },
  watch: {
    dialog(newVal) {
      if (newVal) {
        this.carregarDados()
      } else {
        // Limpar gráficos quando o diálogo for fechado
        this.destroyCharts()
      }
    },
    dadosCongestionamento: {
      handler(newVal) {
        if (newVal.length > 0) {
          this.$nextTick(() => {
            // Renderizar gráfico de linha automaticamente
            setTimeout(() => {
              this.renderizarGraficoLinhaPrincipal()
            }, 200) // Aguardar 200ms para o DOM estar pronto
          })
        }
      },
      deep: true
    },
    // Watcher para mudanças de tema
    '$vuetify.theme.dark': {
      handler() {
        // Re-renderizar gráfico quando o tema mudar
        if (this.dadosCongestionamento.length > 0) {
          this.$nextTick(() => {
            setTimeout(() => {
              this.renderizarGraficoLinhaPrincipal()
            }, 100)
          })
        }
      }
    }
  },
  mounted() {
    this.chartJsInitialized = true
    
    // Verificar se estamos em ambiente Docker
    const isDocker = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
    
    // Aguardar um pouco mais em ambiente Docker
    if (isDocker) {
      setTimeout(() => {
        this.chartKey++
        this.$forceUpdate()
      }, 500)
    }
  },
  
  beforeDestroy() {
    this.destroyCharts()
  },
  
  methods: {
    destroyCharts() {
      // Destruir todos os gráficos registrados
      this.charts.forEach(chart => {
        if (chart && typeof chart.destroy === 'function') {
          try {
            chart.destroy()
          } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Erro ao destruir gráfico:', error)
          }
        }
      })
      this.charts = []
    },
    
    registerChart(chart) {
      if (chart) {
        this.charts.push(chart)
      }
    },
    
    async carregarDados() {
      this.loading = true
      
      // Limpar gráficos existentes antes de carregar novos dados
      this.destroyCharts()
      
      try {
        const response = await this.$hostsService.getCongestionamentoHorarios()
        
        // Processar a estrutura de dados retornada pelo backend
        let dados = []
        if (response && response.data) {
          // Se a resposta tem data, usar data.rows
          dados = response.data.rows || []
        } else if (response && response.rows) {
          // Se a resposta tem rows diretamente
          dados = response.rows || []
        } else if (Array.isArray(response)) {
          // Se a resposta é diretamente um array
          dados = response
        }
        
        // Converter strings para números se necessário
        this.dadosCongestionamento = dados.map(item => {
          const converted = {
            hora_minuto: item.hora_minuto,
            total: parseInt(item.total) || 0,
            vcon: parseInt(item.vcon) || 0,
            vpra: parseInt(item.vpra) || 0,
            vsuc: parseInt(item.vsuc) || 0,
            vsup: parseInt(item.vsup) || 0,
            vsyn: parseInt(item.vsyn) || 0
          }
          return converted
        })
        
        // Calcular o máximo para normalização
        if (this.dadosCongestionamento.length > 0) {
          this.maxTotal = Math.max(...this.dadosCongestionamento.map(item => item.total))
        }
        
        // Forçar re-renderização dos gráficos
        this.$nextTick(async () => {
          // Verificar se estamos em ambiente Docker
          const isDocker = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
          
          this.chartKey++
          this.$forceUpdate()
          
          // Aguardar um pouco mais em ambiente Docker
          const delay = isDocker ? 500 : 100
          
          setTimeout(async () => {
            // Verificar se o canvas está disponível
            await this.verificarCanvas()
            
            this.chartKey++
            this.$forceUpdate()
          }, delay)
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Erro ao carregar dados de congestionamento:', error)
        this.$emit('show-snackbar', 'Erro ao carregar dados de congestionamento', 'error')
      } finally {
        this.loading = false
      }
    },
    getSystemColor(sistema) {
      const colors = {
        'VPRA': 'info',
        'VSUC': 'success',
        'VSUP': 'warning',
        'VCON': 'purple',
        'VSYN': 'teal'
      }
      return colors[sistema] || 'grey'
    },
    getSystemMainColor(sistema) {
      return getSystemMainColor(sistema)
    },
    getPercentual(item) {
      if (this.maxTotal === 0) return 0
      return (item.total / this.maxTotal) * 100
    },
    getPercentualColor(item) {
      const percentual = this.getPercentual(item)
      if (percentual >= 80) return 'error'
      if (percentual >= 60) return 'warning'
      return 'success'
    },
    verificarCanvas() {
      return new Promise((resolve) => {
        this.$nextTick(() => {
          const canvas = document.querySelector('canvas')
          resolve(canvas)
        })
      })
    },
    
    renderizarGraficoLinhaPrincipal() {
      // Verificar se há dados
      if (!this.dadosCongestionamento || this.dadosCongestionamento.length === 0) {
        return
      }
      
      // Encontrar o canvas principal
      const canvas = document.querySelector(`#main-chart-${this.chartKey}`)
      if (!canvas) {
        return
      }
      
      // Ajustar a resolução do canvas para dispositivos de alta densidade
      const dpr = window.devicePixelRatio || 1
      const rect = canvas.getBoundingClientRect()
      
      // Ajustar o tamanho físico do canvas baseado no DPI
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      
      // Ajustar a escala de desenho
      const ctx = canvas.getContext('2d')
      ctx.scale(dpr, dpr)
      
      // Redefinir o estilo CSS para manter o tamanho visual
      canvas.style.width = rect.width + 'px'
      canvas.style.height = rect.height + 'px'
      
      if (!ctx) {
        // eslint-disable-next-line no-console
        console.error('Erro ao obter contexto do canvas')
        return
      }
      
      // Limpar o canvas
      ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr)
      
      // Preparar dados
      const data = this.dadosCongestionamento.map(item => item.total)
      const labels = this.dadosCongestionamento.map(item => item.hora_minuto)
      
      // Configurações do gráfico
      const padding = { 
        top: 60, 
        right: 40, 
        bottom: labels.length > 10 ? 90 : 60, // Mais espaço para os labels
        left: 60 
      }
      const chartWidth = (canvas.width / dpr) - (padding.left + padding.right)
      const chartHeight = (canvas.height / dpr) - (padding.top + padding.bottom)
      const maxValue = Math.max(...data)
      
      // Obter cores baseadas no tema
      const isDark = this.$vuetify.theme.dark
      const colors = {
        background: isDark ? '#1e1e1e' : '#ffffff',
        border: isDark ? '#4a4a4a' : '#e0e0e0',
        grid: isDark ? '#2d2d2d' : '#f0f0f0',
        text: isDark ? '#e0e0e0' : '#333333',
        textSecondary: isDark ? '#b0b0b0' : '#666666',
        title: isDark ? '#ffffff' : '#333333'
      }
      
      // Desenhar fundo
      ctx.fillStyle = colors.background
      ctx.fillRect(0, 0, canvas.width / dpr, canvas.height / dpr)
      
      // Desenhar borda
      ctx.strokeStyle = colors.border
      ctx.lineWidth = 1
      ctx.strokeRect(0, 0, canvas.width / dpr, canvas.height / dpr)
      
      // Desenhar grade horizontal
      ctx.strokeStyle = colors.grid
      ctx.lineWidth = 1
      const gridLines = 5
      for (let i = 0; i <= gridLines; i++) {
        const y = padding.top + (i * chartHeight / gridLines)
        ctx.beginPath()
        ctx.moveTo(padding.left, y)
        ctx.lineTo((canvas.width / dpr) - padding.right, y)
        ctx.stroke()
      }
      
      // Desenhar grade vertical
      for (let i = 0; i <= data.length; i++) {
        const x = padding.left + (i * chartWidth / data.length)
        ctx.beginPath()
        ctx.moveTo(x, padding.top)
        ctx.lineTo(x, (canvas.height / dpr) - padding.bottom)
        ctx.stroke()
      }
      
      // Desenhar linha do gráfico
      ctx.strokeStyle = '#1976D2'
      ctx.lineWidth = 3
      ctx.beginPath()
      
      data.forEach((value, index) => {
        const x = padding.left + (index * chartWidth / (data.length - 1))
        const y = (canvas.height / dpr) - padding.bottom - (value / maxValue * chartHeight)
        
        if (index === 0) {
          ctx.moveTo(x, y)
        } else {
          ctx.lineTo(x, y)
        }
      })
      
      ctx.stroke()
      
      // Desenhar pontos
      ctx.fillStyle = '#1976D2'
      data.forEach((value, index) => {
        const x = padding.left + (index * chartWidth / (data.length - 1))
        const y = (canvas.height / dpr) - padding.bottom - (value / maxValue * chartHeight)
        
        // Círculo externo
        ctx.beginPath()
        ctx.arc(x, y, 8, 0, 2 * Math.PI)
        ctx.fill()
        
        // Círculo interno
        ctx.fillStyle = '#ffffff'
        ctx.beginPath()
        ctx.arc(x, y, 4, 0, 2 * Math.PI)
        ctx.fill()
        ctx.fillStyle = '#1976D2'
        
        // Desenhar valor
        ctx.fillStyle = colors.text
        ctx.font = 'bold 14px Arial'
        ctx.textAlign = 'center'
        ctx.fillText(value.toString(), x, y - 20)
        ctx.fillStyle = '#1976D2'
      })
      
      // Desenhar labels do eixo X
      ctx.fillStyle = colors.textSecondary
      ctx.font = '10px Arial'
      ctx.textAlign = 'center'
      
      // Mostrar todos os horários - sem usar step
      labels.forEach((label, index) => {
        const x = padding.left + (index * chartWidth / (data.length - 1))
        const y = (canvas.height / dpr) - padding.bottom + 20
        
        // Rotacionar labels para acomodar todos os horários
        ctx.save()
        ctx.translate(x, y)
        ctx.rotate(-Math.PI / 4) // Rotacionar 45 graus
        ctx.fillText(label, 0, 0)
        ctx.restore()
      })
      
      // Desenhar valores do eixo Y
      ctx.fillStyle = colors.textSecondary
      ctx.font = '10px Arial'
      ctx.textAlign = 'right'
      for (let i = 0; i <= gridLines; i++) {
        const y = padding.top + (i * chartHeight / gridLines)
        const value = Math.round((gridLines - i) * maxValue / gridLines)
        ctx.fillText(value.toString(), padding.left - 10, y + 3)
      }
      
      // Desenhar título
      ctx.fillStyle = colors.title
      ctx.font = 'bold 18px Arial'
      ctx.textAlign = 'center'
      ctx.fillText('Total de Atualizações por Horário', (canvas.width / dpr) / 2, 25)
    },
    
    getCanvasStyle() {
      const isDark = this.$vuetify.theme.dark
      return {
        width: '100%',
        height: '100%',
        border: `1px solid ${isDark ? '#4a4a4a' : '#e0e0e0'}`,
        background: isDark ? '#1e1e1e' : '#ffffff',
        borderRadius: '8px'
      }
    },
    
    verificarChartJSRenderizado() {
      const canvas = document.querySelector('canvas')
      if (!canvas) {
        return false
      }
      
      try {
        const ctx = canvas.getContext('2d')
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const hasContent = imageData.data.some(pixel => pixel !== 0)
        
        return hasContent
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Erro ao verificar canvas:', error)
        return false
      }
    }
  }
}
</script>

<style scoped>
.v-data-table {
  font-size: 0.875rem;
}

.v-data-table th {
  font-weight: 600;
}

/* Estilos específicos para os gráficos */
.chart-container {
  position: relative;
  height: 100%;
  width: 100%;
}

canvas {
  max-height: 100% !important;
  max-width: 100% !important;
  transition: all 0.3s ease;
}

/* Melhorar aparência do canvas no tema escuro */
.theme-dark canvas {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
}

.theme-dark .chart-container {
  background: rgba(30, 30, 30, 0.1);
  border-radius: 8px;
  padding: 8px;
}

/* Diálogos modernos */
.modern-dialog .v-dialog__content {
  backdrop-filter: blur(5px);
}

.modern-dialog-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25) !important;
  border-radius: 16px !important;
}

.modern-dialog-title {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  padding: 20px 24px !important;
}

/* Tema escuro para diálogos */
.theme-dark .modern-dialog-card {
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.95) 0%, rgba(45, 45, 45, 0.9) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.theme-dark .modern-dialog-title {
  background: linear-gradient(135deg, #2d2d2d 0%, #3d3d3d 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

/* Cards de gráfico modernos */
.modern-chart-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.8) 100%);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 12px !important;
}

.modern-chart-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1) !important;
}

.modern-chart-card .v-card__title {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px 12px 0 0;
}

/* Tema escuro para cards de gráfico */
.theme-dark .modern-chart-card {
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.9) 0%, rgba(45, 45, 45, 0.8) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.theme-dark .modern-chart-card .v-card__title {
  background: linear-gradient(135deg, #2d2d2d 0%, #3d3d3d 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

/* Tabela moderna */
.modern-table {
  border-radius: 12px !important;
  overflow: hidden;
  background: transparent !important;
}

.modern-table .v-data-table__wrapper {
  border-radius: 12px;
}

.modern-table .v-data-table__wrapper table {
  border-radius: 12px;
}

.modern-table .v-data-table__wrapper table thead tr {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.modern-table .v-data-table__wrapper table thead tr th {
  border-bottom: 2px solid rgba(0, 0, 0, 0.1);
  font-weight: 600;
  color: #495057;
}

.modern-table .v-data-table__wrapper table tbody tr {
  transition: all 0.2s ease;
}

.modern-table .v-data-table__wrapper table tbody tr:hover {
  background: rgba(0, 123, 255, 0.05) !important;
  transform: scale(1.01);
}

/* Tema escuro para tabela */
.table-dark .v-data-table__wrapper table thead tr {
  background: linear-gradient(135deg, #2d2d2d 0%, #3d3d3d 100%);
}

.table-dark .v-data-table__wrapper table thead tr th {
  border-bottom: 2px solid rgba(255, 255, 255, 0.1);
  color: #ffffff;
}

.table-dark .v-data-table__wrapper table tbody tr:hover {
  background: rgba(33, 150, 243, 0.1) !important;
}

/* Chips modernos */
.v-chip {
  border-radius: 20px !important;
  transition: all 0.3s ease;
  font-weight: 500;
}

.v-chip:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Botões modernos */
.v-btn {
  border-radius: 8px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-transform: none !important;
  font-weight: 500 !important;
  letter-spacing: 0.5px;
}

.v-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.v-btn.v-btn--contained {
  background: linear-gradient(135deg, var(--v-primary-base) 0%, var(--v-primary-darken1) 100%);
  box-shadow: 0 4px 15px rgba(25, 118, 210, 0.3);
}

.v-btn.v-btn--contained:hover {
  box-shadow: 0 8px 25px rgba(25, 118, 210, 0.4);
}

/* Animações de entrada */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modern-chart-card {
  animation: fadeInUp 0.6s ease-out;
}

/* Ícones dos sistemas redondos */
.system-icon-container {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  border: 2px solid rgba(25, 118, 210, 0.2);
}

.system-icon-container:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-color: rgba(25, 118, 210, 0.4);
}

.system-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

/* Ícones dos cabeçalhos */
.system-icon-container-header {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
  border: 2px solid rgba(25, 118, 210, 0.3);
  transition: all 0.3s ease;
}

.system-icon-container-header:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  border-color: rgba(25, 118, 210, 0.5);
}

.system-icon-header {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid #e0e0e0;
}

/* Tema escuro para ícones dos sistemas */
.theme-dark .system-icon-container {
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(100, 181, 246, 0.2);
}

.theme-dark .system-icon-container:hover {
  border-color: rgba(100, 181, 246, 0.4);
}

.theme-dark .system-icon {
  border-color: rgba(255, 255, 255, 0.2);
}

.theme-dark .system-icon-container-header {
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(100, 181, 246, 0.2);
}

.theme-dark .system-icon-container-header:hover {
  border-color: rgba(100, 181, 246, 0.5);
}

.theme-dark .system-icon-header {
  border-color: rgba(255, 255, 255, 0.2);
}

/* Responsividade melhorada */
@media (max-width: 768px) {
  .modern-dialog-title {
    padding: 16px 20px !important;
  }
  
  .v-btn {
    font-size: 0.875rem !important;
  }
  
  .system-icon-container {
    width: 28px;
    height: 28px;
  }
  
  .system-icon {
    width: 20px;
    height: 20px;
  }
}
</style> 